/**
 * ShareEmailDialog — multi-recipient email composer used for distributing
 * assessments, sharing results, and broadcasting analytics snapshots. Accepts
 * pasted/comma-separated/space-separated addresses and renders them as
 * removable chips.
 *
 * Parent controls `open` + the `onSend(emails, message)` callback. The dialog
 * manages its own email/message state so the parent can reuse it across flows.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, Plus, X, Send, Loader2 } from 'lucide-react';

export const ShareEmailDialog = ({ open, onClose, title, onSend, sending }) => {
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [message, setMessage] = useState('');

  const addEmail = () => {
    const trimmed = emailInput.trim();
    if (trimmed && trimmed.includes('@') && !emails.includes(trimmed)) {
      setEmails(prev => [...prev, trimmed]);
      setEmailInput('');
    }
  };

  const addMultiple = (text) => {
    const parts = text.split(/[,;\s]+/).map(s => s.trim()).filter(s => s.includes('@') && !emails.includes(s));
    if (parts.length) setEmails(prev => [...prev, ...parts]);
    setEmailInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    addMultiple(text);
  };

  const removeEmail = (idx) => setEmails(prev => prev.filter((_, i) => i !== idx));

  const handleSend = () => {
    if (emailInput.trim()) addEmail();
    const allEmails = [...emails];
    if (emailInput.trim() && emailInput.includes('@') && !allEmails.includes(emailInput.trim())) {
      allEmails.push(emailInput.trim());
    }
    if (allEmails.length === 0) { toast.error('Add at least one email'); return; }
    onSend(allEmails, message);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="share-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#002FA7]" />{title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold">Recipients *</Label>
            <p className="text-[10px] text-[#9CA3AF] mb-1.5">Enter emails separated by comma, semicolon, or press Enter</p>
            <div className="flex gap-2">
              <Input
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder="email@example.com"
                className="text-sm"
                data-testid="share-email-input"
              />
              <Button type="button" size="sm" variant="outline" onClick={addEmail} data-testid="add-email-btn">
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
            {emails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="email-chips">
                {emails.map((e, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#002FA7]/10 text-[#002FA7] text-xs">
                    {e}
                    <button onClick={() => removeEmail(i)} className="hover:text-[#FF3B30]" data-testid={`remove-email-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-[10px] text-[#9CA3AF] mt-1">{emails.length} recipient(s) added</p>
          </div>
          <div>
            <Label className="text-xs font-semibold">Personal Message (optional)</Label>
            <Textarea
              rows={2}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Add a personal note to the email..."
              className="text-sm mt-1"
              data-testid="share-message-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleSend} disabled={sending} data-testid="send-share-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Send {emails.length > 0 ? `(${emails.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
